import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import './GestaoSinistros.css';
import {
  FaShieldAlt, FaPlus, FaSearch, FaSyncAlt, FaFileExport,
  FaExclamationTriangle, FaCamera, FaRegClock, FaCheckCircle,
  FaTimes, FaInfoCircle, FaSave, FaCar, FaUser, FaChartBar,
  FaFire, FaFilter, FaTrophy, FaSort, FaArrowUp,
  FaChevronLeft, FaChevronRight, FaBell, FaClipboardList,
} from 'react-icons/fa';
import { toast } from 'react-toastify';

// ─────────────────────────── CONSTANTS ────────────────────────────
const STATUS_META = {
  aberto:     { label: 'Aberto',     cls: 'badge-aberto',     color: '#dc2626' },
  em_analise: { label: 'Em Análise', cls: 'badge-analise',    color: '#ea580c' },
  finalizado: { label: 'Finalizado', cls: 'badge-finalizado', color: '#16a34a' },
  cancelado:  { label: 'Cancelado',  cls: 'badge-cancelado',  color: '#64748b' },
};

const TIPOS_EVENTO = [
  'Colisão', 'Avaria Mecânica', 'Roubo/Furto', 'Incêndio',
  'Alagamento', 'Acidente com Vítima', 'Dano a Terceiros', 'Outro',
];

// ─────────────────────────── HELPERS ─────────────────────────────
const fmtBRL = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const fmtMonth = (v) => {
  if (!v) return '—';
  const d = new Date(v + '-01');
  return d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
};

function calcRiscoScore(sinistros) {
  const peso = {
    'Colisão': 10, 'Acidente com Vítima': 12, 'Roubo/Furto': 8,
    'Incêndio': 9, 'Dano a Terceiros': 7, 'Alagamento': 5,
    'Avaria Mecânica': 3, 'Outro': 4,
  };
  return sinistros.reduce((acc, s) => {
    const p  = peso[s.tipo_evento] || 4;
    const vp = Math.min(Number(s.valor_prejuizo || 0) / 1000, 10);
    return acc + p + vp;
  }, 0);
}

function buildCSV(rows) {
  const header = ['ID','Data','OS','Tipo','Motorista','Veículo','Prejuízo (R$)','Status','Descrição'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [
    header.map(esc).join(';'),
    ...rows.map(s => [
      s.id, fmtDate(s.data_ocorrencia), s.os_codigo ?? '',
      s.tipo_evento ?? '', s.motorista_nome ?? '', s.veiculo_placa ?? '',
      Number(s.valor_prejuizo ?? 0).toFixed(2).replace('.', ','),
      s.status ?? '', (s.descricao_detalhada ?? '').replace(/\s+/g, ' ').trim(),
    ].map(esc).join(';')),
  ].join('\n');
}

function downloadCSV(filename, content) {
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────── SVG CHARTS ──────────────────────────────────
function BarMini({ value, max, color = '#dc2626', height = 8 }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ width: '100%', background: '#f1f5f9', borderRadius: 999, height, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 999,
        transition: 'width 0.6s cubic-bezier(.4,0,.2,1)' }} />
    </div>
  );
}

function SparkLine({ data, color = '#dc2626', height = 48 }) {
  if (!data || data.length < 2) return <div style={{ height }} />;
  const vals = data.map(d => d.value);
  const maxV = Math.max(...vals, 1);
  const minV = Math.min(...vals, 0);
  const range = maxV - minV || 1;
  const w = 160; const h = height;
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((d.value - minV) / range) * (h - 8) - 4;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block', overflow: 'visible' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" />
      {data.map((d, i) => {
        const x = (i / (data.length - 1)) * w;
        const y = h - ((d.value - minV) / range) * (h - 8) - 4;
        return i === data.length - 1
          ? <circle key={i} cx={x} cy={y} r={4} fill={color} />
          : null;
      })}
    </svg>
  );
}

// ─────────────────────────── MODAL ───────────────────────────────
function Modal({ open, title, onClose, width = 860, children }) {
  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="sin-overlay" onMouseDown={onClose}>
      <div className="sin-modal" style={{ maxWidth: width }} onMouseDown={e => e.stopPropagation()}>
        <div className="sin-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FaShieldAlt style={{ color: '#dc2626' }} />
            <span style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{title}</span>
          </div>
          <button className="sin-btn-ghost" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="sin-modal-body">{children}</div>
      </div>
    </div>
  );
}

// ─────────────────── SKELETON / BADGE / KPI ──────────────────────
function SkeletonRow({ cols = 8 }) {
  return (
    <tr className="sin-skel-row">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i}>
          <div className="sin-skel-cell"
            style={{ width: ['60px','50px','110px','140px','100px','90px','90px','70px'][i] || 80 }} />
        </td>
      ))}
    </tr>
  );
}

function StatusBadge({ status }) {
  const st = String(status || 'aberto').toLowerCase();
  const m  = STATUS_META[st] || STATUS_META.aberto;
  return <span className={`sin-badge ${m.cls}`}>{m.label}</span>;
}

function KpiCard({ title, value, sub, icon: Icon, accentColor = '#dc2626', sparkData }) {
  return (
    <div className="sin-kpi-card">
      <div className="sin-kpi-top">
        <div>
          <div className="sin-kpi-label">{title}</div>
          <div className="sin-kpi-value" style={{ color: accentColor }}>{value}</div>
          {sub && <div className="sin-kpi-sub">{sub}</div>}
        </div>
        <div className="sin-kpi-icon" style={{ background: accentColor + '12', color: accentColor }}>
          <Icon />
        </div>
      </div>
      {sparkData && (
        <div style={{ marginTop: 12 }}>
          <SparkLine data={sparkData} color={accentColor} height={40} />
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="sin-field">
      <label className="sin-field-label">
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

// ════════════════════ CREATE FORM ════════════════════════════════
function CreateForm({ onSubmit, onCancel, creating }) {
  const [fields, setFields] = useState({
    os_id: '', veiculo_id: '', motorista_id: '',
    tipo_evento: '', descricao_detalhada: '', valor_prejuizo: '',
    data_ocorrencia: new Date().toISOString().slice(0, 10),
  });
  const [files,     setFiles]     = useState([]);
  const [veiculos,  setVeiculos]  = useState([]);
  const [motoristas,setMotoristas]= useState([]);
  const [loading,   setLoading]   = useState(true);

  const set = (k, v) => setFields(p => ({ ...p, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const [rv, rm] = await Promise.all([
          api.get('/api/vehicles/disponiveis'),
          api.get('/api/drivers/', { params: { limit: 100 } }),
        ]);
        setVeiculos(Array.isArray(rv.data) ? rv.data : []);
        const lista = rm.data?.data ?? rm.data ?? [];
        setMotoristas(Array.isArray(lista) ? lista : []);
      } catch { /* silencioso */ }
      finally { setLoading(false); }
    })();
  }, []);

  const valid = Boolean(fields.tipo_evento && fields.descricao_detalhada && fields.data_ocorrencia);

  const handleSubmit = () => {
    if (!valid) { toast.warn('Preencha os campos obrigatórios.'); return; }
    onSubmit({ fields, files });
  };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {/* Vinculação */}
      <div className="sin-form-section">
        <div className="sin-form-section-title"><FaClipboardList /> Vinculação</div>
        <div className="sin-form-grid-3">
          <Field label="Ordem de Serviço">
            <input className="sin-input" value={fields.os_id}
              onChange={e => set('os_id', e.target.value)} placeholder="Nº da OS (opcional)" />
          </Field>
          <Field label="Veículo">
            <select className="sin-input" value={fields.veiculo_id}
              onChange={e => set('veiculo_id', e.target.value)} disabled={loading}>
              <option value="">— Selecionar veículo —</option>
              {veiculos.map(v => (
                <option key={v.id} value={v.id}>{v.placa} — {v.marca} {v.modelo}</option>
              ))}
            </select>
          </Field>
          <Field label="Motorista">
            <select className="sin-input" value={fields.motorista_id}
              onChange={e => set('motorista_id', e.target.value)} disabled={loading}>
              <option value="">— Selecionar motorista —</option>
              {motoristas.map(m => (
                <option key={m.id} value={String(m.id).replace('MOT-','').replace('USR-','')}>
                  {m.nome}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      {/* Evento */}
      <div className="sin-form-section">
        <div className="sin-form-section-title"><FaExclamationTriangle /> Evento</div>
        <div className="sin-form-grid-3">
          <Field label="Data da Ocorrência" required>
            <input className="sin-input" type="date" value={fields.data_ocorrencia}
              onChange={e => set('data_ocorrencia', e.target.value)} />
          </Field>
          <Field label="Tipo do Evento" required>
            <select className="sin-input" value={fields.tipo_evento}
              onChange={e => set('tipo_evento', e.target.value)}>
              <option value="">— Selecionar tipo —</option>
              {TIPOS_EVENTO.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Valor do Prejuízo Estimado">
            <input className="sin-input" type="number" min="0" step="0.01"
              value={fields.valor_prejuizo}
              onChange={e => set('valor_prejuizo', e.target.value)}
              placeholder="R$ 0,00" />
          </Field>
        </div>
        <div style={{ marginTop: 14 }}>
          <Field label="Descrição Detalhada" required>
            <textarea className="sin-input sin-textarea" rows={4}
              value={fields.descricao_detalhada}
              onChange={e => set('descricao_detalhada', e.target.value)}
              placeholder="Descreva o ocorrido com o máximo de detalhes possível…" />
          </Field>
        </div>
      </div>

      {/* Fotos */}
      <div className="sin-form-section">
        <div className="sin-form-section-title"><FaCamera /> Evidências Fotográficas</div>
        <label className="sin-upload-zone">
          <input type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={e => setFiles(Array.from(e.target.files || []).slice(0, 10))} />
          <FaCamera style={{ fontSize: 22, color: '#94a3b8' }} />
          <span style={{ fontWeight: 700, color: '#475569', marginTop: 6 }}>
            {files.length > 0 ? `${files.length} foto(s) selecionada(s)` : 'Clique ou arraste fotos aqui'}
          </span>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>Máximo 10 imagens · PNG, JPG</span>
        </label>
        {files.length > 0 && (
          <div className="sin-foto-preview">
            {files.map((f, i) => (
              <div key={i} className="sin-foto-chip">
                <FaCamera style={{ fontSize: 11, color: '#6366f1' }} />
                {f.name.length > 20 ? f.name.slice(0, 18) + '…' : f.name}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
        <button className="sin-btn-secondary" onClick={onCancel} disabled={creating}>
          <FaTimes /> Cancelar
        </button>
        <button className="sin-btn-primary" onClick={handleSubmit} disabled={creating || !valid}>
          {creating ? <><FaRegClock /> Salvando…</> : <><FaSave /> Registrar Sinistro</>}
        </button>
      </div>
    </div>
  );
}

// ════════════════════ DETAILS PANEL ══════════════════════════════
function DetailsPanel({ sinistro, canAdmin, onUpdateStatus }) {
  const [notificando, setNotificando] = useState(false);
  const [seguradoras, setSeguradoras] = useState([]);

  const st = String(sinistro.status || 'aberto').toLowerCase();
  const m  = STATUS_META[st] || STATUS_META.aberto;

  let fotos = [];
  try {
    fotos = sinistro.fotos_json
      ? (Array.isArray(sinistro.fotos_json) ? sinistro.fotos_json : JSON.parse(sinistro.fotos_json))
      : [];
  } catch { fotos = []; }

  // Busca seguradoras para poder notificar
  useEffect(() => {
    api.get('/api/integracoes/seguradoras')
      .then(r => setSeguradoras(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const notificarSeguradora = async (segId) => {
    setNotificando(true);
    try {
      await api.post(`/api/integracoes/seguradoras/${segId}/notificar-sinistro`, {
        sinistro_id: sinistro.id,
        mensagem: `Sinistro #${sinistro.id} atualizado. Status: ${st}. Prejuízo estimado: ${fmtBRL(sinistro.valor_prejuizo)}.`,
      });
      toast.success('Seguradora notificada com sucesso!');
    } catch { toast.error('Falha ao notificar seguradora.'); }
    finally { setNotificando(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {/* Hero */}
      <div className="sin-detail-hero" style={{ borderLeftColor: m.color }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: m.color, textTransform: 'uppercase', letterSpacing: 1 }}>
            {sinistro.tipo_evento || 'Evento'}
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', marginTop: 2 }}>
            Sinistro #{sinistro.id}
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            Ocorrido em {fmtDate(sinistro.data_ocorrencia)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: '#dc2626' }}>
            {fmtBRL(sinistro.valor_prejuizo)}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>prejuízo estimado</div>
          <div style={{ marginTop: 6 }}><StatusBadge status={sinistro.status} /></div>
        </div>
      </div>

      {/* Grid de infos */}
      <div className="sin-detail-grid">
        {[
          { label: 'OS Vinculada',   value: sinistro.os_codigo   ? `#${sinistro.os_codigo}`  : '—' },
          { label: 'Motorista',      value: sinistro.motorista_nome || '—' },
          { label: 'Veículo',        value: sinistro.veiculo_placa  || '—' },
          { label: 'Tipo do Evento', value: sinistro.tipo_evento    || '—' },
        ].map(({ label, value }) => (
          <div key={label} className="sin-detail-item">
            <div className="sin-detail-label">{label}</div>
            <div className="sin-detail-value">{value}</div>
          </div>
        ))}
      </div>

      {/* Descrição */}
      <div className="sin-form-section">
        <div className="sin-form-section-title"><FaInfoCircle /> Descrição</div>
        <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.7 }}>
          {sinistro.descricao_detalhada || 'Sem descrição registrada.'}
        </div>
      </div>

      {/* Atualizar status (admin) */}
      {canAdmin && (
        <div className="sin-form-section">
          <div className="sin-form-section-title"><FaSort /> Atualizar Status</div>
          <select className="sin-input" style={{ maxWidth: 280 }} value={st}
            onChange={e => onUpdateStatus(sinistro.id, e.target.value)}>
            <option value="aberto">Aberto</option>
            <option value="em_analise">Em Análise</option>
            <option value="finalizado">Finalizado</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
      )}

      {/* Notificar seguradora */}
      {seguradoras.length > 0 && canAdmin && (
        <div className="sin-form-section" style={{ borderLeft: '4px solid #2563eb' }}>
          <div className="sin-form-section-title"><FaShieldAlt /> Notificar Seguradora</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {seguradoras.map(seg => (
              <button key={seg.id} className="sin-btn-secondary"
                onClick={() => notificarSeguradora(seg.id)}
                disabled={notificando}
                style={{ fontSize: 12 }}>
                <FaShieldAlt style={{ color: '#2563eb' }} />
                {notificando ? 'Enviando…' : `Notificar ${seg.nome}`}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
            Registra o evento de atualização deste sinistro no log de integração da seguradora.
          </div>
        </div>
      )}

      {/* Fotos */}
      <div className="sin-form-section">
        <div className="sin-form-section-title"><FaCamera /> Evidências ({fotos.length})</div>
        {fotos.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>Nenhuma foto registrada.</div>
        ) : (
          <div className="sin-foto-grid">
            {fotos.map((p, i) => (
              <a key={i} href={String(p)} target="_blank" rel="noreferrer" className="sin-foto-card">
                <img src={String(p)} alt="" className="sin-foto-img"
                  onError={e => { e.currentTarget.style.display = 'none'; }} />
                <div className="sin-foto-label">Ver foto {i + 1}</div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════ TAB: DASHBOARD ══════════════════════════════
function TabDashboard({ sinistros, kpis }) {
  const tendencia = useMemo(() => {
    const now = new Date();
    const meses = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return { key, label: fmtMonth(key), value: 0, prejuizo: 0 };
    });
    sinistros.forEach(s => {
      const d = new Date(s.data_ocorrencia);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const mo  = meses.find(x => x.key === key);
      if (mo) { mo.value++; mo.prejuizo += Number(s.valor_prejuizo || 0); }
    });
    return meses;
  }, [sinistros]);

  const porTipo = useMemo(() => {
    const map = {};
    sinistros.forEach(s => { const t = s.tipo_evento || 'Outro'; map[t] = (map[t] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [sinistros]);

  const topMotoristas = useMemo(() => {
    const map = {};
    sinistros.forEach(s => {
      const id = s.motorista_id;
      if (!id) return;
      if (!map[id]) map[id] = { nome: s.motorista_nome || `#${id}`, count: 0, prejuizo: 0, sinistros: [] };
      map[id].count++;
      map[id].prejuizo += Number(s.valor_prejuizo || 0);
      map[id].sinistros.push(s);
    });
    return Object.values(map)
      .map(m => ({ ...m, score: Math.round(calcRiscoScore(m.sinistros)) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [sinistros]);

  const topVeiculos = useMemo(() => {
    const map = {};
    sinistros.forEach(s => {
      const id = s.veiculo_id;
      if (!id) return;
      if (!map[id]) map[id] = { placa: s.veiculo_placa || `#${id}`, count: 0, prejuizo: 0 };
      map[id].count++;
      map[id].prejuizo += Number(s.valor_prejuizo || 0);
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [sinistros]);

  const maxTipo   = Math.max(1, ...porTipo.map(x => x[1]));
  const maxScore  = Math.max(1, ...topMotoristas.map(m => m.score));
  const maxVCount = Math.max(1, ...topVeiculos.map(v => v.count));

  // SVG bar chart
  const maxBar = Math.max(1, ...tendencia.map(t => t.value));
  const barW = 28, barH = 80, gap = 10;
  const totalW = tendencia.length * (barW + gap) - gap;

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {/* KPIs */}
      <div className="sin-kpi-grid">
        <KpiCard title="Em Aberto"     value={kpis.aberto}            sub="Casos pendentes"
          icon={FaExclamationTriangle} accentColor="#dc2626"
          sparkData={tendencia.map(t => ({ value: t.value }))} />
        <KpiCard title="Em Análise"    value={kpis.emAnalise}         sub="Aguardando tratativa"
          icon={FaRegClock}            accentColor="#ea580c" />
        <KpiCard title="Finalizados"   value={kpis.finalizado}        sub="Casos resolvidos"
          icon={FaCheckCircle}         accentColor="#16a34a" />
        <KpiCard title="Prejuízo Total" value={fmtBRL(kpis.prejuTotal)} sub={`${kpis.total} registros`}
          icon={FaChartBar}            accentColor="#6366f1" />
        <KpiCard title="Este Mês"      value={kpis.noMes}             sub="Ocorrências no mês atual"
          icon={FaBell}                accentColor="#0891b2" />
      </div>

      {/* Charts Row */}
      <div className="sin-charts-row">
        {/* Tendência mensal */}
        <div className="sin-chart-card sin-chart-lg">
          <div className="sin-chart-header">
            <span className="sin-chart-title">Evolução Mensal de Sinistros</span>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>Últimos 6 meses</span>
          </div>
          <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
            <svg width={Math.max(totalW, 300)} height={barH + 40} style={{ display: 'block' }}>
              {tendencia.map((mo, i) => {
                const x = i * (barW + gap);
                const h = maxBar > 0 ? (mo.value / maxBar) * barH : 0;
                const y = barH - h;
                const isPeak = mo.value === Math.max(...tendencia.map(t => t.value));
                return (
                  <g key={mo.key}>
                    <rect x={x} y={y} width={barW} height={h} rx={6}
                      fill={isPeak ? '#dc2626' : '#fecaca'}
                      style={{ transition: 'height 0.6s ease' }} />
                    {mo.value > 0 && (
                      <text x={x + barW / 2} y={y - 5} textAnchor="middle"
                        style={{ fontSize: 11, fontWeight: 800, fill: '#374151' }}>{mo.value}</text>
                    )}
                    <text x={x + barW / 2} y={barH + 16} textAnchor="middle"
                      style={{ fontSize: 10, fill: '#94a3b8', fontWeight: 600 }}>{mo.label}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {/* Por tipo */}
        <div className="sin-chart-card sin-chart-sm">
          <div className="sin-chart-header">
            <span className="sin-chart-title">Por Tipo de Evento</span>
          </div>
          {porTipo.length === 0
            ? <div style={{ color: '#94a3b8', fontSize: 13 }}>Sem dados.</div>
            : porTipo.map(([tipo, count], i) => (
              <div key={tipo} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{tipo}</span>
                  <span style={{ fontSize: 13, fontWeight: 900, color: '#dc2626' }}>{count}</span>
                </div>
                <BarMini value={count} max={maxTipo}
                  color={['#dc2626','#ea580c','#f59e0b','#6366f1','#0891b2','#16a34a'][i] || '#dc2626'}
                  height={7} />
              </div>
            ))
          }
        </div>
      </div>

      {/* Ranking Row */}
      <div className="sin-charts-row">
        {/* Motoristas */}
        <div className="sin-chart-card sin-chart-lg">
          <div className="sin-chart-header">
            <span className="sin-chart-title">
              <FaFire style={{ color: '#ea580c', marginRight: 6 }} />Motoristas · Score de Risco
            </span>
            <span style={{ fontSize: 11, background: '#fef2f2', padding: '3px 8px', borderRadius: 8, color: '#dc2626', fontWeight: 700 }}>
              ATENÇÃO
            </span>
          </div>
          {topMotoristas.length === 0
            ? <div style={{ color: '#94a3b8', fontSize: 13 }}>Nenhum motorista vinculado.</div>
            : topMotoristas.map((mo, i) => (
              <div key={mo.nome} className="sin-rank-row">
                <div className="sin-rank-pos" style={{ color: i === 0 ? '#dc2626' : '#94a3b8' }}>
                  {i === 0 ? <FaTrophy style={{ color: '#f59e0b' }} /> : `#${i + 1}`}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{mo.nome}</span>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span style={{ fontSize: 12, color: '#64748b' }}>{mo.count} sinistro{mo.count !== 1 ? 's' : ''}</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#dc2626' }}>{fmtBRL(mo.prejuizo)}</span>
                    </div>
                  </div>
                  <BarMini value={mo.score} max={maxScore}
                    color={i === 0 ? '#dc2626' : i === 1 ? '#ea580c' : '#f59e0b'} height={6} />
                </div>
                <div className="sin-score-badge"
                  style={{ background: i === 0 ? '#fef2f2' : '#fff7ed', color: i === 0 ? '#dc2626' : '#ea580c' }}>
                  {mo.score}
                </div>
              </div>
            ))
          }
        </div>

        {/* Veículos */}
        <div className="sin-chart-card sin-chart-sm">
          <div className="sin-chart-header">
            <span className="sin-chart-title">
              <FaCar style={{ color: '#6366f1', marginRight: 6 }} />Veículos · Ocorrências
            </span>
          </div>
          {topVeiculos.length === 0
            ? <div style={{ color: '#94a3b8', fontSize: 13 }}>Nenhum veículo vinculado.</div>
            : topVeiculos.map(v => (
              <div key={v.placa} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{v.placa}</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{v.count}× · {fmtBRL(v.prejuizo)}</span>
                </div>
                <BarMini value={v.count} max={maxVCount} color="#6366f1" height={6} />
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}

// ═══════════════════ TAB: REGISTROS ══════════════════════════════
function TabRegistros({ sinistros, loading, canAdmin, onOpenCreate, onDetails, onUpdateStatus, fetchError, onRefresh }) {
  const [query,        setQuery]        = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [typeFilter,   setTypeFilter]   = useState('todos');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [page,         setPage]         = useState(1);
  const PAGE_SIZE = 12;

  const tipos = useMemo(() => {
    const s = new Set(sinistros.map(x => x.tipo_evento).filter(Boolean));
    return ['todos', ...Array.from(s).sort()];
  }, [sinistros]);

  const filtered = useMemo(() => {
    const q    = query.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom) : null;
    const to   = dateTo   ? new Date(dateTo)   : null;
    return sinistros.filter(s => {
      const st = (s.status || 'aberto').toLowerCase();
      if (statusFilter !== 'todos' && st !== statusFilter) return false;
      if (typeFilter   !== 'todos' && (s.tipo_evento || '') !== typeFilter) return false;
      if (from || to) {
        const d = new Date(s.data_ocorrencia);
        if (isNaN(d.getTime())) return false;
        if (from && d < from) return false;
        if (to) { const e = new Date(to); e.setHours(23, 59, 59, 999); if (d > e) return false; }
      }
      if (!q) return true;
      return [s.id, s.os_codigo, s.tipo_evento, s.motorista_nome, s.veiculo_placa, s.status, s.descricao_detalhada]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [sinistros, query, statusFilter, typeFilter, dateFrom, dateTo]);

  const total   = filtered.length;
  const totalPg = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pg      = Math.min(page, totalPg);
  const rows    = useMemo(() => filtered.slice((pg - 1) * PAGE_SIZE, pg * PAGE_SIZE), [filtered, pg]);

  const reset   = () => { setQuery(''); setStatusFilter('todos'); setTypeFilter('todos'); setDateFrom(''); setDateTo(''); setPage(1); };
  const exportar = () => { downloadCSV(`sinistros_${new Date().toISOString().slice(0, 10)}.csv`, buildCSV(filtered)); toast.info('CSV gerado.'); };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {/* Toolbar */}
      <div className="sin-toolbar">
        <div className="sin-search-box">
          <FaSearch style={{ color: '#94a3b8', flexShrink: 0 }} />
          <input className="sin-search-input" value={query}
            onChange={e => { setQuery(e.target.value); setPage(1); }}
            placeholder="Buscar por OS, placa, motorista, tipo…" />
        </div>
        <select className="sin-select-filter" value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="todos">Todos os status</option>
          <option value="aberto">Abertos</option>
          <option value="em_analise">Em análise</option>
          <option value="finalizado">Finalizados</option>
          <option value="cancelado">Cancelados</option>
        </select>
        <select className="sin-select-filter" value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          {tipos.map(t => <option key={t} value={t}>{t === 'todos' ? 'Todos os tipos' : t}</option>)}
        </select>
        <input type="date" className="sin-select-filter" value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setPage(1); }} title="De" />
        <input type="date" className="sin-select-filter" value={dateTo}
          onChange={e => { setDateTo(e.target.value); setPage(1); }} title="Até" />
        <button className="sin-btn-ghost" onClick={reset}><FaTimes /> Limpar</button>
        <button className="sin-btn-ghost" onClick={exportar}><FaFileExport /> CSV</button>
        <button className="sin-btn-ghost" onClick={onRefresh}><FaSyncAlt /></button>
        <button className="sin-btn-primary" onClick={onOpenCreate}><FaPlus /> Novo Registro</button>
      </div>

      {fetchError && (
        <div className="sin-alert-error">
          <FaExclamationTriangle />
          <div style={{ flex: 1 }}><strong>Falha ao carregar</strong> — {fetchError}</div>
          <button className="sin-btn-primary" onClick={onRefresh}><FaSyncAlt /> Tentar novamente</button>
        </div>
      )}

      {/* Tabela */}
      <div className="sin-table-wrap">
        <table className="sin-table">
          <thead>
            <tr>
              <th>Data</th><th>OS</th><th>Tipo</th>
              <th>Motorista</th><th>Veículo</th><th>Prejuízo</th>
              <th>Status</th><th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : rows.length === 0
              ? (
                <tr>
                  <td colSpan={8} style={{ padding: '3rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: '#94a3b8' }}>
                      <FaShieldAlt style={{ fontSize: 32, opacity: 0.3 }} />
                      <div style={{ fontWeight: 700, color: '#64748b' }}>Nenhum sinistro encontrado</div>
                      <div style={{ fontSize: 13 }}>Ajuste os filtros ou registre um novo sinistro.</div>
                      <button className="sin-btn-primary" onClick={onOpenCreate} style={{ marginTop: 8 }}>
                        <FaPlus /> Registrar Sinistro
                      </button>
                    </div>
                  </td>
                </tr>
              )
              : rows.map(s => (
                <tr key={s.id} className="sin-table-row">
                  <td style={{ fontWeight: 700, color: '#334155' }}>{fmtDate(s.data_ocorrencia)}</td>
                  <td>
                    <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#6366f1', fontWeight: 700 }}>
                      #{s.os_codigo || 'N/A'}
                    </span>
                  </td>
                  <td><span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{s.tipo_evento || '—'}</span></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#eff6ff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#2563eb', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                        {(s.motorista_nome || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>{s.motorista_nome || '—'}</span>
                    </div>
                  </td>
                  <td>
                    {s.veiculo_placa
                      ? <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 13, color: '#0f172a',
                          background: '#f8fafc', padding: '3px 8px', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                          {s.veiculo_placa}
                        </span>
                      : <span style={{ color: '#94a3b8' }}>—</span>
                    }
                  </td>
                  <td style={{ fontWeight: 800, color: Number(s.valor_prejuizo) > 0 ? '#dc2626' : '#94a3b8' }}>
                    {fmtBRL(s.valor_prejuizo)}
                  </td>
                  <td><StatusBadge status={s.status} /></td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button className="sin-btn-row" onClick={() => onDetails(s)}>
                        <FaInfoCircle /> Ver
                      </button>
                      {canAdmin && (
                        <select className="sin-select-row"
                          value={(s.status || 'aberto').toLowerCase()}
                          onChange={e => onUpdateStatus(s.id, e.target.value)}>
                          <option value="aberto">Aberto</option>
                          <option value="em_analise">Em Análise</option>
                          <option value="finalizado">Finalizado</option>
                          <option value="cancelado">Cancelado</option>
                        </select>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>

        {!loading && total > 0 && (
          <div className="sin-pagination">
            <span style={{ fontSize: 13, color: '#64748b' }}>
              {rows.length} de {total} · pág. {pg}/{totalPg}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="sin-btn-pag" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={pg === 1}>
                <FaChevronLeft />
              </button>
              <button className="sin-btn-pag" onClick={() => setPage(p => Math.min(totalPg, p + 1))} disabled={pg === totalPg}>
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════ TAB: RISCO ═══════════════════════════════════
function TabRisco({ sinistros }) {
  const motoristas = useMemo(() => {
    const map = {};
    sinistros.forEach(s => {
      const id   = s.motorista_id || '__sem__';
      const nome = s.motorista_nome || (s.motorista_id ? `Motorista #${s.motorista_id}` : 'Sem motorista');
      if (!map[id]) map[id] = { id, nome, sinistros: [] };
      map[id].sinistros.push(s);
    });
    return Object.values(map)
      .map(mo => {
        const total    = mo.sinistros.length;
        const prejuizo = mo.sinistros.reduce((a, s) => a + Number(s.valor_prejuizo || 0), 0);
        const score    = Math.round(calcRiscoScore(mo.sinistros));
        const nivel    = score >= 30 ? 'ALTO' : score >= 15 ? 'MÉDIO' : 'BAIXO';
        const cor      = score >= 30 ? '#dc2626' : score >= 15 ? '#ea580c' : '#16a34a';
        const tipos    = {};
        mo.sinistros.forEach(s => { tipos[s.tipo_evento || 'Outro'] = (tipos[s.tipo_evento || 'Outro'] || 0) + 1; });
        return { ...mo, total, prejuizo, score, nivel, cor, tipos };
      })
      .sort((a, b) => b.score - a.score);
  }, [sinistros]);

  const maxScore = Math.max(1, ...motoristas.map(m => m.score));

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#0f172a' }}>Ranking de Risco por Motorista</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Score calculado com base em tipo de evento, frequência e valor do prejuízo.
          </p>
        </div>
        <div className="sin-badge"
          style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontSize: 13, padding: '6px 14px' }}>
          <FaFire style={{ marginRight: 6 }} />{motoristas.filter(m => m.nivel === 'ALTO').length} em nível alto
        </div>
      </div>

      <div className="sin-risco-grid">
        {motoristas.filter(m => m.id !== '__sem__').map(mo => (
          <div key={mo.id} className="sin-risco-card" style={{ borderTopColor: mo.cor }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: mo.cor + '18',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: mo.cor, fontWeight: 900, fontSize: 14 }}>
                  {mo.nome.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{mo.nome}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{mo.total} sinistro{mo.total !== 1 ? 's' : ''}</div>
                </div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 20,
                background: mo.cor + '15', color: mo.cor, border: `1px solid ${mo.cor}40` }}>
                {mo.nivel}
              </span>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: '#64748b' }}>
                <span>Score de Risco</span>
                <span style={{ fontWeight: 900, color: mo.cor }}>{mo.score} pts</span>
              </div>
              <BarMini value={mo.score} max={maxScore} color={mo.cor} height={8} />
            </div>

            <div className="sin-risco-stats">
              <div className="sin-risco-stat">
                <div style={{ fontSize: 18, fontWeight: 900, color: mo.cor }}>{mo.total}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Ocorrências</div>
              </div>
              <div className="sin-risco-stat" style={{ borderLeft: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#334155' }}>{fmtBRL(mo.prejuizo)}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Prejuízo total</div>
              </div>
            </div>

            {Object.keys(mo.tipos).length > 0 && (
              <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  Tipos
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {Object.entries(mo.tipos).map(([t, n]) => (
                    <span key={t} style={{ fontSize: 11, background: '#f8fafc', border: '1px solid #e2e8f0',
                      borderRadius: 8, padding: '3px 8px', color: '#334155', fontWeight: 700 }}>
                      {t} <strong style={{ color: mo.cor }}>×{n}</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {motoristas.filter(m => m.id !== '__sem__').length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
          <FaUser style={{ fontSize: 32, opacity: 0.3 }} />
          <div style={{ marginTop: 12, fontWeight: 700, color: '#64748b' }}>Nenhum motorista vinculado a sinistros</div>
          <div style={{ fontSize: 13 }}>Ao registrar sinistros com motoristas, o ranking será gerado automaticamente.</div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════ TAB: SEGUROS ════════════════════════════════
function TabSeguros({ sinistros }) {
  const [premioMensal, setPremioMensal] = useState('');
  const [seguradoras,  setSeguradoras]  = useState([]);
  const [apolicesSeg,  setApolicesSeg]  = useState([]);
  const [segSelected,  setSegSelected]  = useState('');
  const [loadingSeg,   setLoadingSeg]   = useState(true);

  // Busca seguradoras e apólices do módulo de integrações
  useEffect(() => {
    (async () => {
      setLoadingSeg(true);
      try {
        const { data } = await api.get('/api/integracoes/seguradoras');
        const lista = Array.isArray(data) ? data : [];
        setSeguradoras(lista);
        if (lista.length > 0) {
          const firstId = String(lista[0].id);
          setSegSelected(firstId);
          const { data: ap } = await api.get(`/api/integracoes/seguradoras/${firstId}/apolices`)
            .catch(() => ({ data: [] }));
          setApolicesSeg(Array.isArray(ap) ? ap : []);
        }
      } catch { /* módulo pode não estar ativo */ }
      finally { setLoadingSeg(false); }
    })();
  }, []);

  const handleSegChange = async (segId) => {
    setSegSelected(segId);
    setApolicesSeg([]);
    if (!segId) return;
    try {
      const { data } = await api.get(`/api/integracoes/seguradoras/${segId}/apolices`)
        .catch(() => ({ data: [] }));
      setApolicesSeg(Array.isArray(data) ? data : []);
    } catch {}
  };

  // Prêmio real: soma apólices ativas (quando disponível); caso contrário, usa campo manual
  const premioReal = useMemo(() => {
    if (apolicesSeg.length > 0) {
      return apolicesSeg
        .filter(a => {
          if (a.status && a.status !== 'ativa') return false;
          if (a.vigencia_fim && new Date(a.vigencia_fim) < new Date()) return false;
          return true;
        })
        .reduce((acc, a) => acc + Number(a.premio_mensal || 0), 0);
    }
    return Number(premioMensal || 0);
  }, [apolicesSeg, premioMensal]);

  const totalPrejuizo = useMemo(() =>
    sinistros.reduce((a, s) => a + Number(s.valor_prejuizo || 0), 0), [sinistros]);

  const meses = useMemo(() => {
    const s = new Set(sinistros.map(s => {
      const d = new Date(s.data_ocorrencia);
      return isNaN(d.getTime()) ? null : `${d.getFullYear()}-${d.getMonth()}`;
    }).filter(Boolean));
    return Math.max(1, s.size);
  }, [sinistros]);

  const totalPremio = premioReal * meses;
  const saldo       = totalPremio - totalPrejuizo;

  // Por veículo — prejuízo vs prêmio da apólice (quando vinculada por placa)
  const porVeiculo = useMemo(() => {
    const map = {};
    sinistros.forEach(s => {
      const id = s.veiculo_placa || s.veiculo_id || 'Sem placa';
      if (!map[id]) map[id] = { placa: s.veiculo_placa || id, count: 0, prejuizo: 0, premioTotal: null };
      map[id].count++;
      map[id].prejuizo += Number(s.valor_prejuizo || 0);
    });
    apolicesSeg.forEach(a => {
      const placa = a.veiculo_placa;
      if (placa && map[placa]) map[placa].premioTotal = Number(a.premio_mensal || 0) * meses;
    });
    return Object.values(map).sort((a, b) => b.prejuizo - a.prejuizo).slice(0, 8);
  }, [sinistros, apolicesSeg, meses]);

  const maxPrej = Math.max(1, ...porVeiculo.map(v => v.prejuizo));

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {/* Banner de status da integração */}
      <div className="sin-form-section" style={{ borderLeft: '4px solid #2563eb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 13, background: '#2563eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, flexShrink: 0 }}>
              <FaShieldAlt />
            </div>
            <div>
              <div style={{ fontWeight: 900, color: '#0f172a', fontSize: 15 }}>Integração com Seguradoras</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                {loadingSeg
                  ? 'Verificando integrações...'
                  : seguradoras.length > 0
                  ? `${seguradoras.length} seguradora(s) conectada(s) · ${apolicesSeg.length} apólice(s) ativa(s)`
                  : 'Nenhuma seguradora integrada — configure no módulo Integrações'}
              </div>
            </div>
          </div>
          {seguradoras.length > 0
            ? <div className="sin-badge" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontSize: 13, padding: '7px 16px' }}>
                <FaCheckCircle style={{ marginRight: 6 }} /> Conectado
              </div>
            : <div className="sin-badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontSize: 13, padding: '7px 16px' }}>
                Pendente
              </div>
          }
        </div>
      </div>

      {/* Seletor de seguradora (apenas quando há dados reais) */}
      {seguradoras.length > 0 && (
        <div className="sin-form-section">
          <div className="sin-form-section-title"><FaFilter /> Filtrar por Seguradora</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <select className="sin-input" style={{ maxWidth: 300 }}
              value={segSelected} onChange={e => handleSegChange(e.target.value)}>
              <option value="">Todas as seguradoras</option>
              {seguradoras.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
            {apolicesSeg.length > 0 && (
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                {apolicesSeg.length} apólice(s) · Prêmio mensal base:{' '}
                <strong style={{ color: '#2563eb' }}>{fmtBRL(premioReal)}</strong>
              </span>
            )}
          </div>

          {/* Cards de apólices */}
          {apolicesSeg.length > 0 && (
            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {apolicesSeg.map(a => (
                <div key={a.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Apólice</div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: '#0f172a', marginTop: 3 }}>{a.numero_apolice}</div>
                  <div style={{ fontSize: 14, color: '#2563eb', fontWeight: 800, marginTop: 6 }}>{fmtBRL(a.premio_mensal)}/mês</div>
                  {a.veiculo_placa && (
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                      Placa: <strong>{a.veiculo_placa}</strong>
                    </div>
                  )}
                  {a.vigencia_fim && (
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                      Vigência até {fmtDate(a.vigencia_fim)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Campo manual (apenas quando não há apólices reais) */}
      {apolicesSeg.length === 0 && (
        <div className="sin-form-section">
          <div className="sin-form-section-title"><FaChartBar /> Simulador Manual</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Prêmio Mensal de Seguro (R$)">
              <input className="sin-input" type="number" min="0" step="100"
                value={premioMensal} onChange={e => setPremioMensal(e.target.value)}
                placeholder="Ex: 3500" />
            </Field>
            <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Período analisado</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginTop: 4 }}>
                {meses} mese{meses !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: '#94a3b8' }}>
            Configure uma seguradora em <strong>Integrações → Seguradoras</strong> para importar os prêmios automaticamente por apólice e veículo.
          </div>
        </div>
      )}

      {/* KPIs comparativos */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {[
          { label: 'Total Pago em Prêmios',              value: fmtBRL(totalPremio),          color: '#2563eb', bg: '#eff6ff' },
          { label: 'Total em Prejuízos (Sinistros)',      value: fmtBRL(totalPrejuizo),         color: '#dc2626', bg: '#fef2f2' },
          { label: saldo >= 0 ? 'Saldo (Prêmio > Prejuízo)' : 'Prejuízo Excede Prêmio',
            value: fmtBRL(Math.abs(saldo)),
            color: saldo >= 0 ? '#16a34a' : '#ea580c',
            bg: saldo >= 0 ? '#f0fdf4' : '#fff7ed' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} style={{ background: bg, borderRadius: 14, padding: '1.2rem', textAlign: 'center', border: `1px solid ${color}20` }}>
            <div style={{ fontSize: 22, fontWeight: 900, color }}>{value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 700 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Barra de sinistralidade */}
      {totalPremio > 0 && (
        <div className="sin-chart-card">
          <div className="sin-chart-header">
            <span className="sin-chart-title">Sinistralidade — Prejuízo como % do Prêmio</span>
            <span style={{ fontSize: 14, fontWeight: 900, color: totalPrejuizo > totalPremio ? '#dc2626' : '#16a34a' }}>
              {`${((totalPrejuizo / totalPremio) * 100).toFixed(1)}%`}
            </span>
          </div>
          <div style={{ background: '#f1f5f9', borderRadius: 999, height: 14, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(100, (totalPrejuizo / totalPremio) * 100)}%`,
              height: '100%',
              background: totalPrejuizo > totalPremio ? '#dc2626' : '#16a34a',
              borderRadius: 999, transition: 'width 0.8s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
            <span>0% — Nenhum prejuízo</span>
            <span>100% — Prejuízo = Prêmio</span>
          </div>
        </div>
      )}

      {/* Por veículo */}
      {porVeiculo.length > 0 && (
        <div className="sin-chart-card">
          <div className="sin-chart-header">
            <span className="sin-chart-title">
              <FaCar style={{ color: '#6366f1', marginRight: 6 }} />
              Prejuízo por Veículo{apolicesSeg.length > 0 ? ' vs Prêmio da Apólice' : ''}
            </span>
          </div>
          {porVeiculo.map(v => (
            <div key={v.placa} style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#334155', fontFamily: 'monospace' }}>{v.placa}</span>
                <div style={{ display: 'flex', gap: 14, fontSize: 12 }}>
                  <span style={{ color: '#dc2626', fontWeight: 800 }}>Prej: {fmtBRL(v.prejuizo)}</span>
                  {v.premioTotal != null && (
                    <span style={{ color: '#2563eb', fontWeight: 800 }}>Prêm: {fmtBRL(v.premioTotal)}</span>
                  )}
                </div>
              </div>
              <div style={{ position: 'relative', height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'visible' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%',
                  background: '#dc2626', borderRadius: 999,
                  width: `${Math.min(100, (v.prejuizo / maxPrej) * 100)}%`,
                  transition: 'width 0.6s ease' }} />
                {v.premioTotal != null && (
                  <div style={{ position: 'absolute', left: `${Math.min(99, (v.premioTotal / maxPrej) * 100)}%`,
                    top: -3, height: 14, width: 2, background: '#2563eb', borderRadius: 1, opacity: 0.8 }} />
                )}
              </div>
              {v.premioTotal != null && v.prejuizo > v.premioTotal && (
                <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginTop: 5 }}>
                  ⚠ Prejuízo excede prêmio em {fmtBRL(v.prejuizo - v.premioTotal)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════ MAIN COMPONENT ══════════════════════════════
function hasAdmin(user) {
  return String(user?.perfil || user?.role || '').toLowerCase().includes('admin');
}

export default function GestaoSinistros({ user }) {
  const [sinistros,  setSinistros]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [tab,        setTab]        = useState('dashboard');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating,   setCreating]   = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected,   setSelected]   = useState(null);
  const abortRef = useRef(null);
  const canAdmin = useMemo(() => hasAdmin(user), [user]);

  const fetchSinistros = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setFetchError('');
    try {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      const { data } = await api.get('/api/riscos/sinistros', { signal: abortRef.current.signal });
      setSinistros(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.name === 'AbortError') return;
      setFetchError('Falha ao carregar sinistros.');
      toast.error('Erro ao carregar sinistros.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSinistros(); }, [fetchSinistros]);

  // Socket realtime
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const mod    = await import('socket.io-client');
        const ioFn   = mod?.io || mod?.default;
        if (!ioFn || !mounted) return;
        const socket = ioFn(api?.defaults?.baseURL || undefined, { transports: ['websocket', 'polling'] });
        socket.on('novo_sinistro',      () => { fetchSinistros({ silent: true }); toast.info('Novo sinistro registrado.'); });
        socket.on('seguradoras:webhook', () => { fetchSinistros({ silent: true }); });
        return () => { mounted = false; socket.disconnect(); };
      } catch { /* socket opcional */ }
    })();
    return () => { mounted = false; };
  }, [fetchSinistros]);

  const kpis = useMemo(() => {
    const now  = new Date();
    const norm = sinistros.map(s => ({
      ...s,
      status:        (s.status || 'aberto').toLowerCase(),
      valor_prejuizo: Number(s.valor_prejuizo || 0),
    }));
    return {
      aberto:     norm.filter(s => s.status === 'aberto').length,
      emAnalise:  norm.filter(s => s.status === 'em_analise').length,
      finalizado: norm.filter(s => s.status === 'finalizado').length,
      cancelado:  norm.filter(s => s.status === 'cancelado').length,
      prejuTotal: norm.reduce((a, s) => a + s.valor_prejuizo, 0),
      noMes:      norm.filter(s => {
        const d = new Date(s.data_ocorrencia);
        return !isNaN(d.getTime()) && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length,
      total: norm.length,
    };
  }, [sinistros]);

  const updateStatus = useCallback(async (id, newStatus) => {
    try {
      await api.patch(`/api/riscos/sinistros/${id}/status`, { status: newStatus });
      toast.success('Status atualizado!');
      setSinistros(prev => prev.map(s => s.id === id ? { ...s, status: newStatus } : s));
      if (selected?.id === id) setSelected(prev => prev ? { ...prev, status: newStatus } : prev);
    } catch { toast.error('Erro ao atualizar status.'); }
  }, [selected]);

  const handleCreate = useCallback(async (payload) => {
    setCreating(true);
    try {
      const fd = new FormData();
      Object.entries(payload.fields).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
      (payload.files || []).forEach(f => fd.append('fotos', f));
      const { data } = await api.post('/api/riscos/sinistros', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data?.message || 'Sinistro registrado!');
      setCreateOpen(false);
      fetchSinistros({ silent: true });
    } catch { toast.error('Falha ao registrar sinistro.'); }
    finally { setCreating(false); }
  }, [fetchSinistros]);

  const TABS = [
    { id: 'dashboard', label: 'Dashboard',        icon: FaChartBar     },
    { id: 'registros', label: 'Ocorrências',       icon: FaClipboardList },
    { id: 'risco',     label: 'Risco & Ofensores', icon: FaFire          },
    { id: 'seguros',   label: 'Seguros',            icon: FaShieldAlt     },
  ];

  return (
    <div className="sin-page">
      {/* PAGE HEADER */}
      <div className="sin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="sin-header-icon"><FaShieldAlt /></div>
          <div>
            <h1 className="sin-page-title">Sinistros & Avarias</h1>
            <div className="sin-page-sub">Centro de controle de risco da frota</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="sin-header-kpi">
            <span style={{ fontSize: 22, fontWeight: 900, color: '#dc2626' }}>{kpis.aberto}</span>
            <span style={{ fontSize: 12, color: '#64748b' }}>abertos</span>
          </div>
          <div className="sin-header-kpi">
            <span style={{ fontSize: 22, fontWeight: 900, color: '#0f172a' }}>{fmtBRL(kpis.prejuTotal)}</span>
            <span style={{ fontSize: 12, color: '#64748b' }}>prejuízo total</span>
          </div>
          <button className="sin-btn-primary" onClick={() => setCreateOpen(true)}>
            <FaPlus /> Registrar Sinistro
          </button>
        </div>
      </div>

      {/* TABS NAV */}
      <div className="sin-tabs-nav">
        {TABS.map(t => (
          <button key={t.id} className={`sin-tab-btn${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}>
            <t.icon style={{ fontSize: 13 }} /> {t.label}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div className="sin-tab-content">
        {tab === 'dashboard' && <TabDashboard sinistros={sinistros} kpis={kpis} />}
        {tab === 'registros' && (
          <TabRegistros
            sinistros={sinistros} loading={loading} canAdmin={canAdmin}
            fetchError={fetchError} onRefresh={fetchSinistros}
            onOpenCreate={() => setCreateOpen(true)}
            onDetails={s => { setSelected(s); setDetailOpen(true); }}
            onUpdateStatus={updateStatus}
          />
        )}
        {tab === 'risco'   && <TabRisco   sinistros={sinistros} />}
        {tab === 'seguros' && <TabSeguros sinistros={sinistros} />}
      </div>

      {/* MODAL CRIAR */}
      <Modal open={createOpen} title="Registrar Novo Sinistro"
        onClose={() => setCreateOpen(false)} width={860}>
        <CreateForm onSubmit={handleCreate} onCancel={() => setCreateOpen(false)} creating={creating} />
      </Modal>

      {/* MODAL DETALHES */}
      <Modal
        open={detailOpen}
        title={selected ? `Sinistro #${selected.id} — ${selected.tipo_evento || 'Detalhe'}` : 'Detalhe'}
        onClose={() => { setDetailOpen(false); setSelected(null); }}
        width={900}>
        {selected && (
          <DetailsPanel sinistro={selected} canAdmin={canAdmin} onUpdateStatus={updateStatus} />
        )}
      </Modal>
    </div>
  );
}